import { useState } from "react";
import { Cell } from "../components/Cell";
import { PatientNameFields, PatientStatusFields, usePatientIdentityForm } from "../components/PatientIdentityParts";
import { PATIENT_CONTACT_COLUMNS } from "../model/materielConstants";
import { formatContactPatient } from "../model/followUpRules";
import {
    dangerButtonStyle,
    emptyStyle,
    errorStyle,
    inactivePatientBadgeStyle,
    modalOverlayStyle,
    modalStyle,
    patientEditButtonStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
    tdStyle,
    thStyle,
} from "../styles/materielStyles";
import { usePatientContacts } from "./usePatientContacts";

function ContactRow({ row, onEditPatient, onPatch, onDelete }) {
    return (
        <tr style={{ background: row.notInvoiced ? "#fee2e2" : "white" }}>
            {PATIENT_CONTACT_COLUMNS.map(column => (
                <td key={column.key} style={tdStyle}>
                    {column.key === "patient" ? (
                        <button type="button" onClick={() => onEditPatient(row)} title="Modifier le patient" style={patientEditButtonStyle}>
                            <span>
                                <span style={{ display: "block" }}>{formatContactPatient(row) || "Patient à renseigner"}</span>
                                {row.inactive && <small style={inactivePatientBadgeStyle}>Patient inactif</small>}
                            </span>
                            <span aria-hidden="true" style={{ fontSize: "15px" }}>✎</span>
                        </button>
                    ) : (
                        <Cell row={row} column={column} onChange={value => onPatch(row.id, { [column.key]: value })} />
                    )}
                </td>
            ))}
            <td style={tdStyle}>
                <button onClick={() => onDelete(row.id)} style={dangerButtonStyle}>Supprimer</button>
            </td>
        </tr>
    );
}

function ContactsTableBody({ contacts, onEditPatient }) {
    const { rows, loading, patchRow, deleteRow } = contacts;
    if (loading) return <div style={emptyStyle}>Chargement...</div>;
    if (rows.length === 0) return <div style={emptyStyle}>Aucun contact patient.</div>;
    return (
        <table style={{ borderCollapse: "collapse", minWidth: "1400px", width: "100%" }}>
            <thead>
                <tr>
                    {PATIENT_CONTACT_COLUMNS.map(column => (
                        <th key={column.key} style={{ ...thStyle, width: column.width }}>{column.label}</th>
                    ))}
                    <th style={{ ...thStyle, width: 90 }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(row => (
                    <ContactRow key={row.id} row={row} onEditPatient={onEditPatient} onPatch={patchRow} onDelete={deleteRow} />
                ))}
            </tbody>
        </table>
    );
}

// Tableau des contacts patients du service, une ligne par contact.
export function PatientContactTable({ serviceKey }) {
    const contacts = usePatientContacts(serviceKey);
    const { rows, savingId, error, refresh, addRow, patchRow } = contacts;
    const [editedPatient, setEditedPatient] = useState(null);

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: "10px", borderBottom: "1px solid #f1f5f9" }}>
                <strong style={{ fontSize: "14px", color: "#0f172a" }}>Contact Patient</strong>
                <span style={{ borderRadius: "999px", background: "#f1f5f9", color: "#475569", padding: "2px 8px", fontSize: "12px", fontWeight: 700 }}>{rows.length}</span>
                <div style={{ flex: 1 }} />
                {savingId && <span style={{ color: "#64748b", fontSize: "12px" }}>Enregistrement...</span>}
                <button onClick={refresh} style={secondaryButtonStyle}>Actualiser</button>
                <button onClick={addRow} disabled={savingId === "new"} style={primaryButtonStyle}>Ajouter</button>
            </div>
            {error && <div role="alert" style={errorStyle}>{error}</div>}
            <div style={{ flex: 1, overflow: "auto" }}>
                <ContactsTableBody contacts={contacts} onEditPatient={setEditedPatient} />
            </div>
            {editedPatient && (
                <ContactPatientIdentityModal
                    row={editedPatient}
                    saving={savingId === editedPatient.id}
                    onClose={() => setEditedPatient(null)}
                    onSave={async patch => {
                        await patchRow(editedPatient.id, patch);
                        setEditedPatient(null);
                    }}
                />
            )}
        </div>
    );
}

function ContactPatientIdentityModal({ row, saving, onClose, onSave }) {
    const { form, setField, error, submit } = usePatientIdentityForm(row, onSave);

    return (
        <div role="presentation" style={modalOverlayStyle} onMouseDown={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-patient-modal-title"
                style={modalStyle}
                onMouseDown={event => event.stopPropagation()}
            >
                <h2 id="contact-patient-modal-title" style={{ margin: 0, fontSize: "18px", color: "#111827" }}>
                    Modifier le patient
                </h2>
                <form onSubmit={submit} style={{ display: "grid", gap: "14px" }}>
                    {error && <div role="alert" style={errorStyle}>{error}</div>}
                    <PatientNameFields form={form} setField={setField} />
                    <PatientStatusFields form={form} setField={setField} />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                        <button type="button" onClick={onClose} disabled={saving} style={secondaryButtonStyle}>Annuler</button>
                        <button type="submit" disabled={saving} style={primaryButtonStyle}>
                            {saving ? "Enregistrement..." : "Enregistrer"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
